import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// dayjs.extend is idempotent and global, so extending once here is enough for the app.
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats a date in the given IANA timezone (e.g. "Europe/Zagreb").
 * Falls back to the browser's local timezone when no IANA id is provided.
 */
export const formatInTimeZone = (
  date: dayjs.ConfigType,
  iana: string | null | undefined,
  format: string
): string => {
  if (!date) {
    return "";
  }

  const parsed = dayjs(date);
  if (!parsed.isValid()) {
    return "";
  }

  return iana ? parsed.tz(iana).format(format) : parsed.format(format);
};

// Wall-clock format used to move values across the antd <DatePicker> boundary
// without letting the browser timezone reinterpret the hour.
const WALL_CLOCK_FORMAT = "YYYY-MM-DDTHH:mm:ss";

/**
 * Converts a stored instant (UTC) into the dayjs value an antd DatePicker should
 * display so that the picker shows the tenant timezone's wall-clock time.
 *
 * The returned dayjs carries the same wall-clock numbers as the tenant timezone
 * but is expressed in the browser's local zone, which is what the picker renders.
 * Pair it with {@link pickerValueToUtcIso} on save to round-trip without drift.
 */
export const pickerValueFromUtc = (
  date: dayjs.ConfigType,
  iana: string | null | undefined
): dayjs.Dayjs | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = dayjs(date);
  if (!parsed.isValid()) {
    return undefined;
  }

  if (!iana) {
    return parsed;
  }

  return dayjs(parsed.tz(iana).format(WALL_CLOCK_FORMAT));
};

/**
 * Converts the wall-clock value coming out of an antd DatePicker into a UTC ISO
 * string, interpreting the entered time as belonging to the tenant timezone.
 *
 * This guarantees that an admin who picks e.g. 18:00 for a Saudi Arabia tenant
 * gets 18:00 stored, displayed and emailed in that tenant's timezone, regardless
 * of the admin's own browser timezone.
 */
export const pickerValueToUtcIso = (
  value: dayjs.ConfigType,
  iana: string | null | undefined
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return undefined;
  }

  if (!iana) {
    return parsed.toISOString();
  }

  return dayjs.tz(parsed.format(WALL_CLOCK_FORMAT), iana).toISOString();
};

type WorldClockCity = { label: string; iana: string };

/**
 * Cities shown in the hover tooltip so admins can sanity-check the term time
 * against well-known locations.
 */
export const WORLD_CLOCK_CITIES: WorldClockCity[] = [
  { label: "Zagreb (Croatia)", iana: "Europe/Zagreb" },
  { label: "London (UK)", iana: "Europe/London" },
  { label: "Riyadh (Saudi Arabia)", iana: "Asia/Riyadh" },
  { label: "Dubai (UAE)", iana: "Asia/Dubai" },
  { label: "New York (USA)", iana: "America/New_York" },
];

/**
 * Standard/daylight abbreviations for zones where the browser's Intl API only
 * returns a GMT offset (notably European zones). Falls back to the Intl short
 * name for anything not listed.
 */
const TZ_ABBREVIATIONS: Record<string, { standard: string; daylight: string }> = {
  "Europe/Zagreb": { standard: "CET", daylight: "CEST" },
  "Europe/Belgrade": { standard: "CET", daylight: "CEST" },
  "Europe/Sarajevo": { standard: "CET", daylight: "CEST" },
  "Europe/Ljubljana": { standard: "CET", daylight: "CEST" },
  "Europe/Vienna": { standard: "CET", daylight: "CEST" },
  "Europe/Berlin": { standard: "CET", daylight: "CEST" },
  "Europe/Paris": { standard: "CET", daylight: "CEST" },
  "Europe/Rome": { standard: "CET", daylight: "CEST" },
  "Europe/Madrid": { standard: "CET", daylight: "CEST" },
  "Europe/London": { standard: "GMT", daylight: "BST" },
  "Asia/Riyadh": { standard: "AST", daylight: "AST" },
  "Asia/Kuwait": { standard: "AST", daylight: "AST" },
  "Asia/Qatar": { standard: "AST", daylight: "AST" },
  "Asia/Bahrain": { standard: "AST", daylight: "AST" },
  "Asia/Dubai": { standard: "GST", daylight: "GST" },
  "Asia/Muscat": { standard: "GST", daylight: "GST" },
};

const intlAbbreviation = (date: dayjs.ConfigType, iana: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      timeZoneName: "short",
    }).formatToParts(dayjs(date).toDate());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};

/**
 * Returns the short timezone abbreviation (e.g. "CET", "CEST", "AST") for the
 * given instant in the given IANA zone, accounting for daylight saving time.
 */
export const getTimezoneAbbreviation = (
  date: dayjs.ConfigType,
  iana: string | null | undefined
): string => {
  if (!iana) {
    return "";
  }

  const parsed = dayjs(date);
  if (!parsed.isValid()) {
    return "";
  }

  const mapped = TZ_ABBREVIATIONS[iana];
  if (mapped) {
    const inZone = parsed.tz(iana);
    const janOffset = inZone.month(0).date(1).utcOffset();
    const julOffset = inZone.month(6).date(1).utcOffset();
    const standardOffset = Math.min(janOffset, julOffset);
    const isDaylight = inZone.utcOffset() !== standardOffset;
    return isDaylight ? mapped.daylight : mapped.standard;
  }

  return intlAbbreviation(parsed, iana);
};

/**
 * Builds the list shown in the hover tooltip: the same instant expressed in a
 * handful of well-known cities.
 */
export const getWorldClockTimes = (
  date: dayjs.ConfigType,
  format = "D.MM.YYYY H:mm"
): { label: string; time: string; abbr: string }[] => {
  const parsed = dayjs(date);
  if (!parsed.isValid()) {
    return [];
  }

  return WORLD_CLOCK_CITIES.map((city) => ({
    label: city.label,
    time: parsed.tz(city.iana).format(format),
    abbr: getTimezoneAbbreviation(parsed, city.iana),
  }));
};
